import { apiClientVersioned } from '@/apiClient'
import { WebsitesApi } from '@/apiClient/modules/websites'
import type {
  IWebsiteDetail,
  IWebsiteFile,
  IWebsitePublicHtmlOptions,
} from '@/apiClient/types/websites'

function normalizeFileFetchPath(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return trimmed
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return new URL(trimmed).pathname.replace(/^\/api\/v1/i, '')
    }
  } catch {
    /* use trimmed path below */
  }
  return trimmed.replace(/^\/api\/v1/i, '')
}

/**
 * Load a website's HTML. Prefers the authenticated file URL (JWT) when present —
 * it works regardless of publish/password state. Falls back to the public `/w/{slug}`
 * endpoint only when there's no direct URL; that endpoint serves the website's single
 * file, so there is nothing to select on it.
 */
export async function fetchWebsiteFileHtml(
  website: Pick<IWebsiteDetail, 'slug' | 'is_published'>,
  file: Pick<IWebsiteFile, 'url'>,
  options?: IWebsitePublicHtmlOptions,
): Promise<string> {
  if (file.url) {
    const path = normalizeFileFetchPath(file.url)
    const { data } = await apiClientVersioned.get<string>(path, {
      responseType: 'text',
    })
    return data
  }

  return WebsitesApi.getPublicHtml(website.slug, options)
}
